import { forwardRef, useImperativeHandle } from 'react';
import { FormLabel, GovFormWeb } from '@gov-design-system-ce/react-form';
import { MarkdownRender, ProfilROSSkeleton } from '@gov-nx/component/web';
import { usePersonCommunicationData } from '@gov-nx/core/hooks';
import { useLocale } from '@gov-nx/core/service';
import {
	EmailRadioOption,
	PageCode,
	PhoneRadioOption,
	RosComplaintVariant,
	useProfilROSReklamaceContextInstance,
} from '@gov-nx/module/page';
import {
	AgendaSearchAutocomplete,
	Button,
	FormCheckbox,
	FormDatePicker,
	FormInput,
	FormRadio,
	FormSelect,
	FormTextarea,
	FormWidthElementLayout,
	LayoutGap,
	optionsToChildren,
	Space,
} from '@gov-nx/ui/web';
import { toDateReadable } from '@gov-nx/utils/common';
import { SouhlasCheckbox } from '../../Autorizace/SouhlasCheckbox';
import { AddressSearchForm } from './AddressSearchForm';

const ValueOrEmpty = ({
	label,
	value,
}: {
	label: string;
	value?: string | number;
}) => {
	const { t } = useLocale(PageCode['profil-ROS']);

	return (
		<>
			{label}
			<Space />
			<span className="font-bold">
				{value ? value : t('formular.pole.neuvedeno')}
			</span>
		</>
	);
};

export interface ProfileROSComplaintViewRef {
	submit: () => void;
}

export interface ComplaintProps {
	code: PageCode;
}

export const ProfileROSComplaintView = forwardRef<
	ProfileROSComplaintViewRef,
	ComplaintProps
>(({ code }, ref) => {
	const {
		data,
		variant,
		controls,
		setControls,
		formDefinition,
		onSubmit,
		filteredOptions,
		interruptionOptions,
		resetAddressSearchFields,
		isLowAuthorization,
	} = useProfilROSReklamaceContextInstance();
	const { emailVerified, phoneVerified } = usePersonCommunicationData();
	const isProcessing = controls.processLoading;
	const { t } = useLocale(code);

	useImperativeHandle(ref, () => ({
		submit: onSubmit,
	}));

	if (controls.initialLoading) {
		return <ProfilROSSkeleton />;
	}

	return (
		<GovFormWeb
			formDefinition={formDefinition}
			onSubmit={onSubmit}>
			<LayoutGap gap="l">
				<MarkdownRender>{t('formular.popis')}</MarkdownRender>
				<FormWidthElementLayout type="l">
					<LayoutGap gap="xl">
						{variant === RosComplaintVariant.BASIC_INFO ? (
							<>
								<FormInput
									field={{
										name: 'ico',
										isDisabled: true,
										placeholder: t('formular.placeholder.ico'),
									}}
									label={{
										children: (
											<ValueOrEmpty
												label={t('formular.pole.ico')}
												value={data?.ico}
											/>
										),
									}}
								/>
								<FormSelect
									field={{
										name: 'pravniForma',
										isDisabled: isProcessing,
										children: (
											<>
												<option value="">
													{t('formular.placeholder.pravni-forma')}
												</option>
												{filteredOptions.map((o) => (
													<option
														value={o.value}
														key={o.value}>
														{o.label}
													</option>
												))}
											</>
										),
									}}
									label={{
										children: (
											<ValueOrEmpty
												label={t('formular.pole.pravni-forma')}
												value={data?.pravniForma?.nazev}
											/>
										),
									}}
								/>

								<FormInput
									field={{
										name: 'stav',
										isDisabled: true,
										placeholder: t('formular.placeholder.stav'),
									}}
									label={{
										children: (
											<ValueOrEmpty
												label={t('formular.pole.stav')}
												value={data?.pravniStav?.nazev}
											/>
										),
									}}
								/>
							</>
						) : null}
						{variant === RosComplaintVariant.AGENDA ? (
							<>
								<FormInput
									field={{
										name: 'nazevSpolecnosti',
										isDisabled: isProcessing,
										placeholder: t('formular.placeholders.nazev-spolecnosti'),
									}}
									label={{
										children: (
											<ValueOrEmpty
												label={t('formular.pole.nazev-spolecnosti')}
												value={data.nazevSpolecnosti}
											/>
										),
									}}
								/>

								<AgendaSearchAutocomplete
									field={{
										name: 'agenda',
										isDisabled: isProcessing,
										placeholder: t('formular.placeholder.agenda'),
									}}
									label={{
										children: (
											<ValueOrEmpty
												label={t('formular.pole.agenda')}
												value={data.agenda?.nazev}
											/>
										),
									}}
								/>
								<FormDatePicker
									field={{
										name: 'datumOd',
										isDisabled: isProcessing,
										placeholder: t('formular.placeholder.datum-vzniku'),
									}}
									label={{
										children: (
											<ValueOrEmpty
												label={t('formular.pole.datum-vzniku')}
												value={
													data?.datumVzniku
														? toDateReadable(data.datumVzniku)
														: undefined
												}
											/>
										),
									}}
								/>
								<FormDatePicker
									field={{
										name: 'datumDo',
										isDisabled: isProcessing,
										placeholder: t('formular.placeholder.datum-zaniku'),
									}}
									label={{
										children: (
											<ValueOrEmpty
												label={t('formular.pole.datum-zaniku')}
												value={
													data?.datumZaniku
														? toDateReadable(data.datumZaniku)
														: undefined
												}
											/>
										),
									}}
								/>

								<LayoutGap gap="s">
									<FormLabel>
										<ValueOrEmpty
											label={t('formular.pole.adresa-sidla')}
											value={data?.sidloSpolecnosti?.adresaText}
										/>
									</FormLabel>
									{!controls.displaySearchAddress ? (
										<Button
											onClick={() =>
												setControls({ displaySearchAddress: true })
											}
											isDisabled={isProcessing}
											color="primary"
											size="m"
											type="outlined">
											{t('akce.zmenit-adresu')}
										</Button>
									) : (
										<>
											<AddressSearchForm
												code={code}
												isProcessing={isProcessing}
											/>
											<Button
												onClick={() => {
													setControls({ displaySearchAddress: false });
													resetAddressSearchFields();
												}}
												isDisabled={isProcessing}
												color="primary"
												size="m"
												type="outlined">
												{t('akce.zrusit-zmeny')}
											</Button>
										</>
									)}
								</LayoutGap>
								<FormSelect
									field={{
										name: 'pozastaveniPreruseni',
										isDisabled: isProcessing,
										children: optionsToChildren(interruptionOptions),
									}}
									label={{
										children: (
											<ValueOrEmpty
												label={t('formular.pole.pozastaveni-preruseni')}
												value={
													data?.pozastaveniPreruseni
														? t('formular.pole.pozastaveni.preruseni.ano')
														: t('formular.pole.pozastaveni.preruseni.ne')
												}
											/>
										),
									}}
								/>
							</>
						) : null}
					</LayoutGap>
				</FormWidthElementLayout>

				<div>
					<FormLabel isLegend={true}>
						{t('formular.pole.kontaktni-udaje')}
					</FormLabel>
					<FormLabel isRequired={true}>
						{t('formular.pole.kontaktni-udaje-popis')}
					</FormLabel>
				</div>
				<FormWidthElementLayout
					type="xl"
					className="flex flex-col gap-4">
					<FormCheckbox
						field={{
							name: 'kontaktTelefon',
							isRequired: false,
							isDisabled: isProcessing,
							label: {
								children: t('formular.pole.poskytnuti-telefonu'),
							},
						}}
					/>
					{formDefinition.formMethods.watch('kontaktTelefon') ? (
						<LayoutGap className="ml-10">
							{phoneVerified ? (
								<FormRadio
									field={{
										name: '_telefon',
										isDisabled: isProcessing,
										options: [
											{
												value: phoneVerified,
												label: {
													children: phoneVerified,
												},
											},
											{
												value: PhoneRadioOption.ANOTHER_PHONE,
												label: {
													children: t('formular.pole.jiny-telefon'),
												},
											},
										],
									}}
									label={{
										children: t('formular.pole.vyberte-ze-seznamu'),
									}}
								/>
							) : (
								<FormInput
									field={{
										name: 'newPhone',
										isDisabled: isProcessing,
										placeholder: t('formular.placeholders.novy-telefon'),
									}}
									label={{ children: t('formular.pole.novy-telefon') }}
								/>
							)}
						</LayoutGap>
					) : null}
					{formDefinition.formMethods.watch('_telefon') ===
						PhoneRadioOption.ANOTHER_PHONE &&
					formDefinition.formMethods.watch('kontaktTelefon') ? (
						<LayoutGap className="ml-10">
							<FormInput
								field={{
									name: 'newPhone',
									isDisabled: isProcessing,
									placeholder: t('formular.placeholders.novy-telefon'),
								}}
								label={{ children: t('formular.pole.novy-telefon') }}
							/>
						</LayoutGap>
					) : null}

					<FormCheckbox
						field={{
							name: 'kontaktEmail',
							isRequired: false,
							isDisabled: isProcessing,
							label: {
								children: t('formular.pole.poskytnuti-emailu'),
							},
						}}
					/>
					{formDefinition.formMethods.watch('kontaktEmail') ? (
						<LayoutGap className="ml-10">
							{emailVerified ? (
								<FormRadio
									field={{
										name: '_email',
										isDisabled: isProcessing,
										options: [
											{
												value: emailVerified,
												label: { children: emailVerified },
											},
											{
												value: EmailRadioOption.ANOTHER_EMAIL,
												label: {
													children: t('formular.pole.jiny-email'),
												},
											},
										],
									}}
									label={{
										children: t('formular.pole.vyberte-ze-seznamu'),
									}}
								/>
							) : (
								<FormInput
									field={{
										name: 'newEmail',
										isDisabled: isProcessing,
										placeholder: t('formular.placeholders.novy-email'),
									}}
									label={{ children: t('formular.pole.novy-email') }}
								/>
							)}
						</LayoutGap>
					) : null}
					{formDefinition.formMethods.watch('_email') ===
						EmailRadioOption.ANOTHER_EMAIL &&
					formDefinition.formMethods.watch('kontaktEmail') ? (
						<LayoutGap className="ml-10">
							<FormInput
								field={{
									name: 'newEmail',
									isDisabled: isProcessing,
									placeholder: t('formular.placeholders.novy-email'),
								}}
								label={{ children: t('formular.pole.novy-email') }}
							/>
						</LayoutGap>
					) : null}
				</FormWidthElementLayout>
				<FormWidthElementLayout
					type="l"
					className="flex flex-col gap-6">
					<FormTextarea
						field={{
							name: 'popisReklamace',
							placeholder: t('formular.placeholders.popis-reklamace'),
							rows: 3,
						}}
						label={{ children: t('formular.pole.popis-reklamace') }}
					/>
				</FormWidthElementLayout>
				<SouhlasCheckbox
					show={isLowAuthorization}
					disabled={isProcessing}
				/>
			</LayoutGap>
		</GovFormWeb>
	);
});
